/**
 * A base class for all API classes. It keeps track of the base URL and the current authentication token.
 * It has helper methods for making authenticated requests based on fetch(), and to make requests that
 * are parsed to JSON. The fetching methods raise errors for non-200 responses. The errors are of type
 * ApiError and contain the response status code and the response body.
 */

import { UseQueryOptions, skipToken } from "@tanstack/react-query";

import { ApiError } from "./api-error";

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
// Our use query options are the same as react-query's, but with the queryKey and queryFn required
// and queryFn takes no arguments.
export type OurQueryFn<T> = (() => Promise<T>) | typeof skipToken;
export interface OurUseQueryOptions<T, QueryFn extends OurQueryFn<T> = OurQueryFn<T>>
  extends WithRequired<UseQueryOptions<T, ApiError, T>, "queryKey"> {
  queryFn: QueryFn;
}

export class ApiBaseClass {
  protected baseUrl: string;
  protected authToken: string | undefined;
  protected accessToken: string | undefined;

  constructor(baseUrl: string, authToken?: string, accessToken?: string) {
    this.baseUrl = baseUrl;
    this.authToken = authToken;
    this.accessToken = accessToken;
  }

  setAuthToken(authToken: string | undefined) {
    this.authToken = authToken;
  }

  async fetchJson(url: string, init?: RequestInit): Promise<unknown> {
    const response = await this.fetch(url, init);
    if (response.status === 204) return;
    return await response.json();
  }

  createQueryOptions<T>(queryOptions: OurUseQueryOptions<T>): OurUseQueryOptions<T> {
    // Should be overridden by subclasses
    return queryOptions;
  }

  async fetch(url: string, init?: RequestInit): Promise<Response> {
    const defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const actualHeaders: RequestInit["headers"] = {
      ...defaultHeaders,
      ...(init?.headers ?? {}),
      ...(this.accessToken
        ? { Authorization: this.accessToken }
        : this.authToken
        ? { Authorization: `Bearer ${this.authToken}` }
        : {}),
    };

    if (
      "Content-Type" in actualHeaders &&
      actualHeaders["Content-Type"] === "multipart/form-data"
    ) {
      // This header is set automatically together with the 'boundary' by fetch, leave it out!
      delete actualHeaders["Content-Type"];
    }

    const response = await this.baseFetch(this.baseUrl + url, {
      ...init,
      headers: actualHeaders,
    });

    if (!response.ok) {
      throw new ApiError(response.status, await response.text());
    }

    return response;
  }

  async baseFetch(url: string, init?: RequestInit): Promise<Response> {
    return fetch(url, init);
  }
}
